import { forwardRef, useContext } from 'react';
import ReactDatePicker from 'react-datepicker';
import cls from 'classnames';
import { useWeekStartsOn } from '@s20/hooks/tenant.hooks';
import { FormContext, FormLabel } from '../FormElements';
import './index.scss';

const defaultOnChange = () => { };

/**
 * react-datepicker without the form-group chrome, for the few places that need a bare picker. The calendar
 * opens Sunday-first (the library default) unless `tenantWeekStart` is set, which opens it on the tenant's
 * "Week starts on" day: pass it from pickers that choose a dispatch week. Use this, not react-datepicker directly.
 */
export const TenantDatePicker = forwardRef(({ tenantWeekStart = false, ...props }, ref) => {
    const weekStartsOn = useWeekStartsOn();

    return (
        <ReactDatePicker
            ref={ref}
            calendarStartDay={tenantWeekStart ? weekStartsOn : undefined}
            {...props}
        />
    );
});

const CustomInput = forwardRef(({ error, ...restProps }, ref) => (
    <>
        <input
            {...restProps}
            ref={ref}
            type="text"
        />

        {Boolean(error) && (
            <small className="invalid-feedback text-danger">
                {error}
            </small>
        )}
    </>
));

export const DatePicker = ({
    id,
    label,
    name,
    selected,
    required = false,
    formGroupClassName = '',
    className = '',
    error,
    onChange = defaultOnChange,
    ...restProps
}) => {
    const context = useContext(FormContext);

    let fieldValue = selected;
    let fieldOnChange = onChange;

    if (context && context.values) {
        fieldValue = context.values[name || id];
        fieldOnChange = (e, v) => {
            onChange(e, v);
            context.onChange(e, v);
        };
    }

    return (
        <div className={cls('form-group', formGroupClassName)}>
            {Boolean(label) && (
                <FormLabel
                    htmlFor={id}
                    text={label}
                    required={required}
                />
            )}

            <TenantDatePicker
                id={id}
                className={cls('form-control', className)}
                name={name}
                required={required}
                selected={fieldValue}
                autoComplete="off"
                onChange={(dateValue, event) => {
                    const newEvent = {
                        ...(event || {}),
                        target: {
                            ...(event?.target || {}),
                            name: event?.target?.name || name,
                            value: dateValue,
                        },
                    };

                    fieldOnChange(newEvent, dateValue);
                }}
                customInput={<CustomInput error={error} />}
                {...restProps}
            />
        </div>
    );
};